import { createSelector } from 'reselect'
import { isInstantBookingLEHotelItem } from 'lib/checkout/checkoutUtils'
import { getFullOffers } from 'selectors/offerSelectors'
import getLEPackageItemTotals from 'checkout/lib/utils/accommodation/getLEPackageItemTotals'
import { checkoutWithMemberPrice } from 'checkout/selectors/view/luxPlusSubscription'
import { isLpcHotelOffer } from 'lib/offer/offerTypes'
import { getPackageUniqueKey } from 'lib/offer/offerUtils'
import {
  createLereSurchargePayloadForCheckoutItem,
  getLereDynamicPricingGroup,
  getLereDynamicPricingGroupForOffer,
  getLereDynamicPricingSurchargeTokenForCheckoutItem,
} from 'checkout/lib/utils/lereDynamicPricing'
import { getPayableAccommodationSurcharge } from 'checkout/lib/utils/accommodation/priceView'
import { applyCancellationOptionPriceFactorToPrices, payableDateChangeDifference } from 'lib/offer/cancellationOptionPricing'
import { getProtectedFlashDateChangePrice } from 'checkout/lib/utils/accommodation/getHotelDateChangePrice'
import { isDerbysoftReservation } from 'lib/order/orderItemUtils'

/**
 * Calculate the payment schedule adjustment amount for hotel items
 * This is used to determine the adjustment amount BEFORE making the API call
 */
const getPaymentScheduleAdjustmentAmount = createSelector(
  (state: App.State) => state.checkout.cart.existingOrder,
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => getFullOffers(state),
  (state: App.State) => state.calendar.calendarsByOccupancy,
  (state: App.State) => state.calendar.calendarsByOccupancyLereGroups,
  (state: App.State) => state.offer.offerAvailableRatesByOccupancy,
  (state: App.State) => state.offer.offerAvailableRatesLereGroups,
  (state: App.State) => checkoutWithMemberPrice(state),
  (state: App.State) => state.checkout.cart.mode,
  (state: App.State) => state.offer.lereSurchargeTokens,
  (state: App.State) => getLereDynamicPricingGroup(state),
  (
    existingOrder,
    cartItems,
    offers,
    calendarsByOccupancy,
    calendarsByOccupancyLereGroups,
    availableRatesByOccupancy,
    offerAvailableRatesLereGroups,
    checkoutWithMemberPrice,
    cartMode,
    lereSurchargeTokens,
    lereGroup,
  ): number | undefined => {
    if (!existingOrder) {
      return undefined
    }

    const hotelItem = cartItems.find(isInstantBookingLEHotelItem)

    if (!hotelItem?.orderItemId) {
      return undefined
    }

    const orderItem = existingOrder.items.find(item => item.id === hotelItem.orderItemId)
    const offer = offers[hotelItem.offerId]

    if (!orderItem || (offer?.type !== 'hotel' && !isLpcHotelOffer(offer))) {
      return undefined
    }

    // LPC (tactical_ao_hotel) packages require roomRateId for unique identification
    const originPackage = isLpcHotelOffer(offer)
      ? offer.packages.find(pkg => pkg.uniqueKey === getPackageUniqueKey(hotelItem.packageId, hotelItem.duration, hotelItem.roomRateId))
      : offer.packages.find(pkg => pkg.id === hotelItem.packageId && pkg.duration === hotelItem.duration)

    if (!originPackage) {
      return undefined
    }
    const offerLereGroup = getLereDynamicPricingGroupForOffer(offer, lereGroup)

    // Calculate new totals using the same logic as getLEPackageItemView
    const packageItemTotals = getLEPackageItemTotals(
      offer,
      originPackage,
      hotelItem.checkIn,
      hotelItem.checkOut,
      hotelItem.occupancy,
      undefined,
      calendarsByOccupancy,
      availableRatesByOccupancy,
      {
        calendarsByOccupancyLereGroups,
        offerAvailableRatesLereGroups,
        lereGroup: offerLereGroup,
      },
      {
        calendarsByOccupancyLereGroups,
        offerAvailableRatesLereGroups,
        expectedLereGroup: undefined,
      },
    )
    const itemTotals = packageItemTotals && applyCancellationOptionPriceFactorToPrices(packageItemTotals, hotelItem.cancellationOptionPriceFactor)

    if (!itemTotals) {
      return undefined
    }

    // Apply any overrides from the cart item
    let price = itemTotals.price
    let surcharge = itemTotals.surcharge

    if (hotelItem.newPrice !== undefined) {
      price = hotelItem.newPrice
    }
    if (hotelItem.newSurcharge !== undefined) {
      surcharge = hotelItem.newSurcharge + (hotelItem.newExtraGuestSurcharge ?? itemTotals.extraGuestSurcharge ?? 0)
    }
    if (hotelItem.newExtraGuestSurcharge !== undefined) {
      // The override is our share only; totals carry the whole surcharge
      itemTotals.extraGuestSurcharge = hotelItem.newExtraGuestSurcharge + (itemTotals.otherFees?.extraGuestSurcharge ?? 0)
    }

    const lereSurchargeToken = getLereDynamicPricingSurchargeTokenForCheckoutItem(
      hotelItem,
      offer,
      originPackage.roomType?.id,
      offerLereGroup,
      lereSurchargeTokens,
    )
    const awaitingLereSurchargeToken = !itemTotals.dynamicPricingSurchargeIncludedInSource &&
      !!createLereSurchargePayloadForCheckoutItem(hotelItem, offer, originPackage.roomType?.id, offerLereGroup) &&
      (!lereSurchargeToken || lereSurchargeToken.status === 'pending')
    if (awaitingLereSurchargeToken) {
      return undefined
    }

    if (lereSurchargeToken?.status === 'success' && !itemTotals.dynamicPricingSurchargeIncludedInSource) {
      itemTotals.dynamicPricingSurcharge = lereSurchargeToken.surchargeAmount
    }
    const payableSurcharge = getPayableAccommodationSurcharge({
      surcharge,
      dynamicPricingSurcharge: itemTotals.dynamicPricingSurcharge,
    })

    // Calculate new price based on cart mode
    let newPrice = 0

    if (cartMode === 'select-date') {
      // For BNBL select-date: preserve original package price
      const priceToUse = orderItem.packagePrice
      const memberPriceToUse = orderItem.packagePrice
      const nextPrice = checkoutWithMemberPrice && memberPriceToUse > 0 ? memberPriceToUse : priceToUse
      newPrice = nextPrice + ((itemTotals.isAvailable ? payableSurcharge : orderItem.reservation?.surcharge) || 0)
    } else if (cartMode === 'change-dates' || cartMode === 'add-extra-guests') {
      const extraGuestSurchargePayableAtProperty = !!originPackage.roomRate?.extraGuestSurcharges?.some(surcharge => surcharge.payableAtProperty)
      const protectedPrice = getProtectedFlashDateChangePrice({
        orderItem,
        duration: hotelItem.duration,
        checkIn: hotelItem.checkIn,
        checkOut: hotelItem.checkOut,
        packageUniqueKey: originPackage.uniqueKey,
        isLpc: isLpcHotelOffer(offer),
        isDerbysoftDateChange: isDerbysoftReservation(orderItem),
        surcharge: (itemTotals.isAvailable ? payableSurcharge : orderItem.reservation?.surcharge) || 0,
        operation: cartMode,
        extraGuestSurcharge: (itemTotals.isAvailable ? itemTotals.extraGuestSurcharge - (itemTotals.otherFees?.extraGuestSurcharge ?? 0) : orderItem.reservation?.extraGuestSurcharge) ?? 0,
        extraGuestSurchargePayableAtProperty,
      })
      if (protectedPrice !== undefined) {
        newPrice = protectedPrice
      } else {
        const newPackageSelected = orderItem.package.duration !== hotelItem.duration || orderItem.package.uniqueKey !== originPackage.uniqueKey
        let priceToUse = newPackageSelected ? price : orderItem.packagePrice
        if (orderItem.channelMarkupId || isLpcHotelOffer(offer)) {
          priceToUse = price
        }
        const memberPriceToUse = (newPackageSelected || isLpcHotelOffer(offer)) ? itemTotals.memberPrice : orderItem.packagePrice
        const nextPrice = checkoutWithMemberPrice && memberPriceToUse > 0 ? memberPriceToUse : priceToUse
        newPrice = nextPrice + ((itemTotals.isAvailable ? payableSurcharge : orderItem.reservation?.surcharge) || 0)
      }
    } else if (hotelItem.isUpdated || cartMode === 'change-package') {
      // For change-package and other updated items
      const nextPrice = checkoutWithMemberPrice && itemTotals.memberPrice > 0 ? itemTotals.memberPrice : price
      newPrice = nextPrice + payableSurcharge
    }

    // Calculate the adjustment amount (only for modes that trigger payment schedule adjustments)
    if (cartMode === 'change-dates' || cartMode === 'add-extra-guests') {
      return payableDateChangeDifference(newPrice - orderItem.total, orderItem)
    }
    if (cartMode === 'select-date' || cartMode === 'change-package') {
      return newPrice - orderItem.total
    }

    return undefined
  },
)

export default getPaymentScheduleAdjustmentAmount
